import React from 'react';
import { Box, Typography, FormControlLabel, Checkbox, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/formatCurrency';

/**
 * Read-only preview of a menu item's add-on options with prices.
 *
 * Add-ons are organized in the backend as categories → options; we flatten them
 * for display. Each option's checkbox reflects whether it is marked as a default
 * choice — it is disabled here. All editing (names, prices, defaults, add/remove)
 * happens in the AddonManagementModal, surfaced via the "Manage" link.
 */
const AddOnsChecklist = ({ addons, loading, currency }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Box sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          {t('common.loading', 'Loading...')}
        </Typography>
      </Box>
    );
  }

  const allOptions = (addons || []).flatMap((cat) =>
    (cat.options || []).map((opt) => ({ ...opt, categoryName: cat.categoryName }))
  );

  if (allOptions.length === 0) {
    return (
      <Typography sx={{ fontSize: 13, color: 'text.secondary', py: 1 }}>
        {t('menuV2.edit.noAddons', 'No add-ons yet. Use Manage to create them.')}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {allOptions.map((opt) => {
        return (
          <Box
            key={opt.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingBlock: 0.5,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!opt.isDefault}
                  disabled
                />
              }
              label={
                <Typography sx={{ fontSize: 13.5, color: 'text.primary' }}>
                  {opt.name}
                  {opt.isDefault && (
                    <Typography component="span" sx={{ fontSize: 11.5, color: 'text.secondary', ml: 0.75 }}>
                      {t('admin.addons.default', 'Default')}
                    </Typography>
                  )}
                </Typography>
              }
              sx={{ m: 0 }}
            />
            <Typography sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 500 }}>
              {opt.price > 0 ? formatCurrency(opt.price, currency) : t('common.free', 'Free')}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default AddOnsChecklist;
